import React, { useState, useEffect } from 'react';
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { speakText, getUserVerbositySetting, TTS_VERBOSITY, isIOSDevice } from '../../helpers/textToSpeech';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';

const TTSEnablementPrompt = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);
  
  const voicePackingEnabled = useSelector(state => state.user?.settings?.data?.voice_packing);

  useEffect(() => {
    const checkShouldShowPrompt = async () => {
      try {
        if (!isIOSDevice()) {
          setShouldShowPrompt(false);
          return;
        }

        if (!voicePackingEnabled) {
          setShouldShowPrompt(false);
          return;
        }

        const currentVerbosity = await getUserVerbositySetting();
        
        if (currentVerbosity === TTS_VERBOSITY.NONE) {
          setShouldShowPrompt(false);
          return;
        }

        const ttsPromptShown = await AsyncStorage.getItem('ttsPromptShown');
        const ttsEnabled = await AsyncStorage.getItem('ttsEnabled');
        const voicePackingLastState = await AsyncStorage.getItem('voicePackingLastState');
        
        if (voicePackingLastState === 'false' && voicePackingEnabled) {
          await AsyncStorage.removeItem('ttsPromptShown');
          setShouldShowPrompt(true);
          await AsyncStorage.setItem('voicePackingLastState', 'true');
          return;
        }
        
        await AsyncStorage.setItem('voicePackingLastState', voicePackingEnabled ? 'true' : 'false');

        if (ttsPromptShown === 'true') {
          setShouldShowPrompt(false);
          return;
        }

        setShouldShowPrompt(true);
        
      } catch (error) {
        console.error('Error checking TTS prompt conditions:', error);
        setShouldShowPrompt(false);
      }
    };

    checkShouldShowPrompt();
  }, [voicePackingEnabled]);

  if (!shouldShowPrompt) {
    return null;
  }

  const handleEnableVoice = async () => {
    setIsLoading(true);
    
    try {
      const result = await speakText('Voice enabled', { volume: 0.01 });
      
      if (result.success) {
        await AsyncStorage.setItem('ttsPromptShown', 'true');
        await AsyncStorage.setItem('ttsEnabled', 'true');
        setShouldShowPrompt(false);
      } else {
        console.warn('Failed to enable TTS:', result.error);
        setShouldShowPrompt(false);
      }
    } catch (error) {
      console.error('Error enabling TTS:', error);
      setShouldShowPrompt(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      await AsyncStorage.setItem('ttsPromptShown', 'true');
      await AsyncStorage.setItem('ttsEnabled', 'false');
      setShouldShowPrompt(false);
    } catch (error) {
      console.error('Error saving TTS preference:', error);
      setShouldShowPrompt(false);
    }
  };

  return (
    <View style={styles.responseView}>
      <LinearGradient
        locations={[0, 1]}
        start={{ x: 0, y: 1 }}
        end={{ x: 0, y: 0 }}
        colors={['#d47d02', '#ffb636']}
        style={styles.promptContainer}>
        
        {/* Close icon */}
        <View style={styles.cancelIcon}>
          <TouchableOpacity testID="SkipTTS" onPress={handleSkip}>
            <i
              className="icon-cancel"
              style={{ fontSize: 26, color: '#1C1C1C' }}>
            </i>
          </TouchableOpacity>
        </View>

        {/* Title */}
        <View style={styles.titleContainer}>
          <View style={styles.iconContainer}>
            <Text style={styles.speakerIcon}>🔊</Text>
          </View>
          <Text style={styles.titleText}>Enable Voice Guidance</Text>
          <Text style={styles.subtitleText}>
            Get audio instructions for better scanning experience
          </Text>
        </View>

        {/* Button */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity 
            testID="EnableVoiceButton"
            onPress={handleEnableVoice}
            disabled={isLoading}
          >
            <LinearGradient
              locations={[0, 0.7, 1]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              colors={['#ffb636', '#ffb636', '#d47d02']}
              style={[styles.actionButton, isLoading && styles.disabledButton]}>
              <Text style={styles.buttonText}>
                {isLoading ? 'Enabling...' : 'Enable Voice'}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>

      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  responseView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1000,
    width: '100%',
    height: '100%'
  },
  promptContainer: {
    backgroundColor: '#d3dfea',
    paddingHorizontal: 20,
    paddingVertical: 15,
    width: '90%',
    maxWidth: 400,
    marginTop: 0,
    borderColor: '#e8971a',
    borderWidth: 3,
    borderRadius: 5,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    position: 'relative'
  },
  cancelIcon: {
    display: 'flex',
    alignSelf: 'flex-end'
  },
  titleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    marginTop: 10
  },
  iconContainer: {
    marginBottom: 10
  },
  speakerIcon: {
    fontSize: 32,
    textAlign: 'center'
  },
  titleText: {
    fontFamily: 'Poppins_300Light',
    fontSize: 20,
    textAlign: 'center',
    color: '#000',
    fontWeight: '500',
    marginBottom: 8
  },
  subtitleText: {
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    textAlign: 'center',
    color: '#1C1C1C',
    lineHeight: 20
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  actionButton: {
    borderWidth: 1,
    borderColor: '#1C1C1C',
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 32,
    minWidth: 200,
    alignItems: 'center',
    justifyContent: 'center'
  },
  disabledButton: {
    opacity: 0.7
  },
  buttonText: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 16,
    textShadowColor: 'rgba(0, 0, 0, 0.25)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
    fontWeight: '500'
  }
});

export default TTSEnablementPrompt;